// Base URL для Strapi API
// По умолчанию используем чистый /api
const normalizeBaseUrl = (url: string): string => url.replace(/\/+$/, '')

const getStrapiApiUrl = () => {
  const configuredApiUrl = process.env.NEXT_PUBLIC_STRAPI_URL
  if (typeof window !== 'undefined') {
    // Клиентская сторона: всегда относительный API путь
    return normalizeBaseUrl(configuredApiUrl || '/api')
  }

  if (configuredApiUrl) {
    return normalizeBaseUrl(configuredApiUrl)
  }

  if (process.env.NODE_ENV === 'production') {
    // На сервере в production формируем абсолютный URL через основной домен
    const baseUrl = process.env.NEXT_PUBLIC_URL
    if (baseUrl) {
      return `${normalizeBaseUrl(baseUrl)}/api`
    }
  }

  return 'http://localhost:1337/api'
}

// Base URL для Strapi изображений (uploads)
const getStrapiImageUrl = (imagePath: string): string => {
  if (!imagePath) return ''
  
  // Если уже полный URL (http/https)
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath
  }
  
  const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`

  // База для медиа = origin Strapi (включая префикс вроде /strapi).
  // Берём её из NEXT_PUBLIC_STRAPI_URL, убирая хвост /api.
  // Работает одинаково на клиенте и сервере (значение инлайнится при сборке):
  //  - "https://t-code.ru/strapi/api" -> "https://t-code.ru/strapi" -> ".../strapi/uploads/..."
  //  - "/strapi/api"                  -> "/strapi"                   -> "/strapi/uploads/..."
  //  - "/api"                         -> ""                          -> "/uploads/..." (относительный)
  const configuredApiUrl = process.env.NEXT_PUBLIC_STRAPI_URL
  if (configuredApiUrl) {
    const mediaBase = normalizeBaseUrl(configuredApiUrl).replace(/\/api$/, '')
    return `${mediaBase}${normalizedPath}`
  }

  if (process.env.NODE_ENV === 'production') {
    const baseUrl = process.env.NEXT_PUBLIC_URL
    if (baseUrl) {
      return `${normalizeBaseUrl(baseUrl)}${normalizedPath}`
    }
  }

  return `http://localhost:1337${normalizedPath}`
}

// Устаревшая переменная, используй getStrapiApiUrl() вместо нее
const STRAPI_URL = getStrapiApiUrl()

export interface StrapiArticle {
  id: number
  documentId: string
  title: string
  slug: string
  shortDescription: string
  description?: any // Rich Text объект
  content?: any // Rich Text объект
  featuredImage?: {
    id: number
    documentId: string
    name: string
    alternativeText?: string
    caption?: string
    url: string
    width: number
    height: number
    formats?: {
      thumbnail?: { url: string }
      small?: { url: string }
      medium?: { url: string }
      large?: { url: string }
    }
  }
  contentImages?: Array<{
    id: number
    url: string
    alternativeText?: string
    width?: number
    height?: number
    formats?: any
  }> | {
    url: string
    alternativeText?: string
    width?: number
    height?: number
  } | {
    data: Array<{
      attributes: {
        url: string
        alternativeText?: string
      }
    }>
  }
  publishedDate: string
  views: number
  seoTitle?: string
  seoDescription?: string
  isExternal: boolean
  externalUrl?: string
  externalSource?: string
  faq?: Array<{ question: string; answer: string }>
  category?: 'Промышленность' | 'Финансы' | 'Юридические услуги' | 'Строительство' | 'Ритейл' | 'Другое'
  isBlock?: boolean
  blocks?: any[] // Dynamic zone blocks для блочной версии статьи
}

export async function getArticles(): Promise<StrapiArticle[]> {
  try {
    const apiUrl = getStrapiApiUrl()
    const url = `${apiUrl}/articles?populate=*&sort=publishedDate:desc`
    const response = await fetch(url, { 
      cache: 'no-store',
      next: { revalidate: 0 }
    })
    if (!response.ok) {
      throw new Error('Failed to fetch articles')
    }
    const data = await response.json()
    return data.data || []
  } catch (error) {
    return []
  }
}

export async function getArticle(slug: string): Promise<StrapiArticle | null> {
  try {
    const baseUrl = getStrapiApiUrl()
    
    // Первый запрос: стандартный populate=* (чтобы не получить 400)
    let mainResponse = await fetch(
      `${baseUrl}/articles?filters[slug][$eq]=${slug}&populate=*`,
      { cache: 'no-store' }
    )
    
    if (!mainResponse.ok) {
      throw new Error('Failed to fetch article')
    }
    
    const mainData = await mainResponse.json()
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Article main data length:', mainData?.data?.length || 0)
    }
    
    const article = mainData.data?.[0]
    
    if (!article) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Article not found for slug', slug, 'response:', JSON.stringify(mainData, null, 2))
      }
      return null
    }
    
    // Если блочная статья, делаем второй запрос только за блоками (без доп. медиа, чтобы не ловить 400)
    if (article.isBlock) {
      const originalBlocks = Array.isArray(article.blocks) ? article.blocks : []
      if (process.env.NODE_ENV === 'development') {
        console.log('Article blocks from main (original):', originalBlocks.length, originalBlocks.map((b: any) => b?.__component))
      }
      const blocksPopulateParams = [
        'populate[blocks][on][content-block-list.horisontal][populate]=*',
        'populate[blocks][on][content-text-block.two-colums-h4][populate]=*',
        'populate[blocks][on][content-text-block.two-colums-paragraphs-titles][populate]=*',
        'populate[blocks][on][content-text-block.two-colums-paragraphs][populate]=*',
        'populate[blocks][on][content-text-block.number-infographics][populate]=*',
        'populate[blocks][on][content-slider.gallery][populate][gallery_items][populate]=*',
        'populate[blocks][on][content-block-list.cards-grid][populate][cards_grid_items][populate]=*',
        'populate[blocks][on][content.main-block][populate]=*',
        'populate[blocks][on][content.title-block][populate]=*',
        'populate[blocks][on][content.description-block][populate]=*',
        'populate[blocks][on][content.dual-img-screen][populate]=*',
        'populate[blocks][on][content.list-block][populate]=*',
        'populate[blocks][on][content.full-screen-img][populate]=*'
      ].join('&')
      
      
      const blocksUrlDeep = `${baseUrl}/articles?filters[slug][$eq]=${slug}&${blocksPopulateParams}`
      let blocksResp = await fetch(blocksUrlDeep, { cache: 'no-store' })
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Blocks deep response status:', blocksResp.status, blocksResp.ok)
      }
      
      if (!blocksResp.ok) {
        // fallback на populate=* для блоков
        const body = await blocksResp.text().catch(() => '')
        if (process.env.NODE_ENV === 'development') {
          console.log('Blocks deep failed, fallback to blocks populate=*. Body:', body)
        }
        blocksResp = await fetch(
          `${baseUrl}/articles?filters[slug][$eq]=${slug}&populate[blocks][populate]=*`,
          { cache: 'no-store' }
        )
      }
      
      if (blocksResp.ok) {
        const blocksData = await blocksResp.json()
        const fetched = blocksData.data?.[0]?.blocks
        // Если получили непустой список блоков, используем их (они уже в правильном порядке)
        // и обогащаем данными из originalBlocks если нужно
        if (fetched && fetched.length > 0) {
          // Создаём мапу оригинальных блоков по id для быстрого доступа
          const originalMap = new Map()
          originalBlocks.forEach((b: any) => {
            if (b?.id) originalMap.set(b.id, b)
          })
          // Мерджим данные из оригинала в fetched, сохраняя порядок fetched
          article.blocks = fetched.map((fb: any) => {
            const orig = fb?.id ? originalMap.get(fb.id) : null
            return orig ? { ...orig, ...fb } : fb
          })
        } else {
          article.blocks = originalBlocks
        }
        if (process.env.NODE_ENV === 'development') {
          console.log('Article blocks after deep fetch:', fetched?.length || 0, fetched?.map((b: any) => b.__component))
          if (fetched?.[0]) {
            console.log('First block sample (deep):', JSON.stringify(fetched[0], null, 2))
          }
        }
      } else {
        // Если запрос не ок - возвращаем исходные блоки из первого запроса
        article.blocks = originalBlocks
      }
    }
    
    // Отладка: смотрим, что пришло
    if (article.isBlock && process.env.NODE_ENV === 'development') {
      console.log('Article isBlock:', article.isBlock)
      console.log('Article blocks after fetch (final):', article.blocks?.length || 0, article.blocks?.map((b: any) => b.__component))
      if (article.blocks?.[0]) {
        console.log('First article block sample (final):', JSON.stringify(article.blocks[0], null, 2))
      }
    }
    
    return article
  } catch (error) {
    return null
  }
}

export interface StrapiCasePage {
  id: number
  documentId: string
  slug: string
  headline: string
  description: string
  cover_img_desktop?: {
    id: number
    documentId: string
    name: string
    alternativeText?: string
    caption?: string
    url: string
    width: number
    height: number
    formats?: {
      thumbnail?: { url: string }
      small?: { url: string }
      medium?: { url: string }
      large?: { url: string }
    }
  }
  cover_img_mob?: {
    id: number
    documentId: string
    name: string
    alternativeText?: string
    caption?: string
    url: string
    width: number
    height: number
    formats?: {
      thumbnail?: { url: string }
      small?: { url: string }
      medium?: { url: string }
      large?: { url: string }
    }
  }
  blocks?: any[] // Dynamic zone blocks
  publishedAt?: string
  createdAt?: string
  updatedAt?: string
}

export async function getCasePage(slug: string): Promise<StrapiCasePage | null> {
  try {
    const baseUrl = getStrapiApiUrl()
    
    // Первый запрос: основные данные с изображениями (populate=* загружает медиа-поля)
    const mainUrl = `${baseUrl}/case-pages?filters[slug][$eq]=${slug}&populate=*`
    
    const mainResponse = await fetch(mainUrl, { 
      cache: 'no-store',
      next: { revalidate: 0 }
    })
    
    if (!mainResponse.ok) {
      throw new Error(`Failed to fetch case page: ${mainResponse.status}`)
    }
    
    const mainData = await mainResponse.json()
    
    if (!mainData.data || mainData.data.length === 0) {
      return null
    }
    
    const casePage = mainData.data[0]
    
    // Временный лог для отладки - проверяем, что возвращает первый запрос
    if (process.env.NODE_ENV === 'development') {
      console.log('First request - has blocks:', !!casePage.blocks)
      console.log('First request - blocks count:', casePage.blocks?.length || 0)
      if (casePage.blocks && Array.isArray(casePage.blocks)) {
        const tableBlock = casePage.blocks.find((b: any) => b.__component === 'content.table')
        if (tableBlock) {
          console.log('First request table block:', JSON.stringify(tableBlock, null, 2))
        } else {
          console.log('First request - table block not found')
        }
      }
    }
    
    const blocksPopulateParams = [
      'populate[blocks]=*',
      'populate[blocks][on][section-case.case-review][populate]=*',
      'populate[blocks][on][section-case.case-review][populate][client][populate]=*',
      'populate[blocks][on][section-case.case-review][populate][services][populate][services_item][populate]=*',
      'populate[blocks][on][section-case.case-review][populate][direction][populate][direction_item][populate]=*',
      'populate[blocks][on][section-case.case-review][populate][infographics][populate]=*',
      'populate[blocks][on][section-case.case-review][populate][button][populate]=*',
      'populate[blocks][on][section-case.goal-objectives][populate]=*',
      'populate[blocks][on][content-text-block.number-infographics][populate]=*',
      'populate[blocks][on][content-text-block.two-colums-h4][populate]=*',
      'populate[blocks][on][content-text-block.two-colums-paragraphs-titles][populate]=*',
      'populate[blocks][on][content-text-block.two-colums-thesis][populate]=*',
      'populate[blocks][on][content-text-block.two-colums-paragraphs][populate]=*',
      'populate[blocks][on][content-text-block.parapraph-wide][populate]=*',
      'populate[blocks][on][content-text-block.parapraph-full][populate]=*',
      'populate[blocks][on][content-block-list.with-options][populate]=*',
      'populate[blocks][on][content-block-list.with-options][populate][with_options_items][populate]=*',
      'populate[blocks][on][content-block-list.with-options][populate][with_options_items][populate][icon][populate]=*',
      'populate[blocks][on][content-block-list.horisontal][populate]=*',
      'populate[blocks][on][content.table][populate][columns][populate]=*',
      'populate[blocks][on][content.table][populate][rows][populate][cells][populate]=*',
      'populate[blocks][on][content-block-list.cards-grid][populate]=*',
      'populate[blocks][on][content-block-list.cards-grid][populate][cards_grid_items][populate]=*',
      'populate[blocks][on][content-block-list.cards-grid][populate][cards_grid_items][populate][icon][populate]=*',
      'populate[blocks][on][content-tabs.block-accordion][populate]=*',
      'populate[blocks][on][content-tabs.left-tabs-with-content][populate]=*',
      'populate[blocks][on][content-tabs.left-tabs-with-content][populate][left_tabs_with_content_items][populate]=*',
      'populate[blocks][on][content-tabs.left-tabs-with-content][populate][left_tabs_with_content_items][populate][icon][populate]=*',
      'populate[blocks][on][content-tabs.faq][populate]=*',
      'populate[blocks][on][content.full-screen-img][populate]=*',
      'populate[blocks][on][content-slider.gallery][populate]=*',
      'populate[blocks][on][content-slider.gallery][populate][gallery_items][populate]=*',
      'populate[blocks][on][content-slider.gallery][populate][gallery_items][populate][img_desktop][populate]=*',
      'populate[blocks][on][content-slider.gallery][populate][gallery_items][populate][img_mobile][populate]=*',
      'populate[blocks][on][content-slider.cards-slider][populate]=*',
      'populate[blocks][on][content-slider.cards-slider][populate][cards_slider_items][populate]=*',
      'populate[blocks][on][content-slider.cards-slider][populate][cards_slider_items][populate][image][populate]=*',
      'populate[blocks][on][content-slider.cards-slider-without-number][populate]=*',
      'populate[blocks][on][content-slider.cards-slider-without-number][populate][cards_slider_without_number_items][populate]=*',
      'populate[blocks][on][content-slider.cards-slider-without-number][populate][cards_slider_without_number_items][populate][image][populate]=*',
      'populate[blocks][on][content-review.two-columns-review][populate]=*',
      'populate[blocks][on][content-review.reviews-slider][populate]=*',
      'populate[blocks][on][content-review.reviews-slider][populate][reviews_slider_items][populate]=*',
      'populate[blocks][on][content-review.reviews-slider][populate][reviews_slider_items][populate][icon][populate]=*',
      'populate[blocks][on][content-cta-banner.full-screen-img][populate]=*',
      'populate[blocks][on][content-cta-banner.left][populate]=*',
      'populate[blocks][on][content-cta-banner.fill-left][populate]=*'
    ].join('&')
    
    const blocksUrl = `${baseUrl}/case-pages?filters[slug][$eq]=${slug}&${blocksPopulateParams}`
    
    // Временный лог для отладки
    if (process.env.NODE_ENV === 'development') {
      console.log('Table populate URL:', blocksUrl)
    }
    
    try {
      const blocksResponse = await fetch(blocksUrl, { 
        cache: 'no-store',
        next: { revalidate: 0 }
      })
      
      if (blocksResponse.ok) {
        const blocksData = await blocksResponse.json()
        
        // Временный лог для отладки
        if (process.env.NODE_ENV === 'development') {
          console.log('Blocks response data:', JSON.stringify(blocksData.data?.[0]?.blocks, null, 2))
        }
        
        if (blocksData.data && blocksData.data.length > 0 && blocksData.data[0].blocks) {
          // Мерджим данные: используем блоки из второго запроса, но сохраняем медиа из первого
          const enrichedBlocks = blocksData.data[0].blocks
          
          // Если в первом запросе были блоки, мерджим их с обогащенными
          if (casePage.blocks && Array.isArray(casePage.blocks) && casePage.blocks.length > 0) {
            // Создаем мапу обогащенных блоков
            const enrichedMap = new Map()
            enrichedBlocks.forEach((block: any) => {
              const key = block.id ? `id_${block.id}` : (block.__component ? `comp_${block.__component}_${enrichedBlocks.indexOf(block)}` : null)
              if (key) enrichedMap.set(key, block)
            })
            
            // Обновляем блоки из первого запроса обогащенными данными
            // Глубоко мерджим объекты, чтобы сохранить все поля
            casePage.blocks = casePage.blocks.map((block: any, index: number) => {
              const key = block.id ? `id_${block.id}` : (block.__component ? `comp_${block.__component}_${index}` : null)
              if (key && enrichedMap.has(key)) {
                const enriched = enrichedMap.get(key)
                // Для таблицы: сохраняем columns из оригинала, rows из обогащенных (с cells)
                if (block.__component === 'content.table') {
                  // Проверяем, есть ли columns в обогащенных данных
                  const hasColumns = enriched.columns && Array.isArray(enriched.columns) && enriched.columns.length > 0
                  const hasRows = enriched.rows && Array.isArray(enriched.rows) && enriched.rows.length > 0
                  
                  return {
                    ...enriched,
                    ...block,
                    // Используем columns из обогащенных, если они есть, иначе из оригинала
                    columns: hasColumns ? enriched.columns : (block.columns || []),
                    // Используем rows из обогащенных (они содержат cells)
                    rows: hasRows ? enriched.rows : (block.rows || [])
                  }
                }
                // Для остальных блоков: используем обогащенные данные
                return { ...block, ...enriched }
              }
              return block
            })
          } else {
            casePage.blocks = enrichedBlocks
          }
        }
      }
    } catch (error) {
      // Используем базовые блоки из первого запроса
    }
    
    // Преобразуем данные в нужный формат
    // Обрабатываем изображения - Strapi 5 возвращает { data: { attributes: {...} } } или напрямую { url: ... }
    const formatImage = (img: any) => {
      if (!img) return undefined

      // Если Strapi отдаёт объект: { data: { attributes: {...} } }
      if (img.data?.attributes) {
        return {
          url: img.data.attributes.url,
          alternativeText: img.data.attributes.alternativeText || '',
          width: img.data.attributes.width,
          height: img.data.attributes.height,
        }
      }

      // Если Strapi отдаёт напрямую { url: ... }
      if (img.url) return img

      return undefined
    }
    
    const formattedDesktop = formatImage(casePage.cover_img_desktop)
    const formattedMob = formatImage(casePage.cover_img_mob)
    
    const formattedCasePage: StrapiCasePage = {
      id: casePage.id,
      documentId: casePage.documentId,
      slug: casePage.slug,
      headline: casePage.headline,
      description: casePage.description,
      cover_img_desktop: formattedDesktop,
      cover_img_mob: formattedMob,
      blocks: casePage.blocks || [],
      publishedAt: casePage.publishedAt,
      createdAt: casePage.createdAt,
      updatedAt: casePage.updatedAt
    }
    
    return formattedCasePage
  } catch (error) {
    return null
  }
}

// Функция для получения названия кейса из Strapi (для breadcrumbs)
export async function getCasePageTitle(slug: string): Promise<string> {
  try {
    const baseUrl = getStrapiApiUrl()
    
    const url = `${baseUrl}/case-pages?filters[slug][$eq]=${slug}&fields[0]=headline`
    const response = await fetch(url, { 
      cache: 'no-store',
      next: { revalidate: 0 }
    })
    
    if (!response.ok) {
      throw new Error('Failed to fetch case page')
    }
    
    const data = await response.json()
    const headline = data.data?.[0]?.headline
    
    if (!headline) {
      return 'Кейс'
    }
    
    // Убираем HTML теги из headline для breadcrumbs
    const textOnly = headline.replace(/<[^>]*>/g, '').trim()
    return textOnly || 'Кейс'
  } catch (error) {
    return 'Кейс'
  }
}

// Экспортируем функцию для использования в других компонентах
export { getStrapiImageUrl }
